import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: email.trim() });
      toast.success(data.message || 'Check your email for the reset link.');
      setSubmittedEmail(email.trim());
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Unable to send reset email. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/billportal-logo.png" alt="BillPortal" className="h-10 w-10" />
            <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              BillPortal
            </h1>
          </div>
          <p className="text-muted-foreground text-sm uppercase tracking-wide font-medium">
            Take control over your Utilities
          </p>
        </div>

        <div className="bg-card border border-border rounded-none p-8">
          <Link
            to="/login"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Link>

          {!submitted ? (
            <>
              <h2 className="text-2xl font-bold tracking-tight mb-2">Forgot your password?</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Enter the email associated with your account and we&apos;ll send you a secure link to reset your password. The link will expire in 15 minutes.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wide font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    autoFocus
                    className="rounded-none mt-1"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="send-reset-link-button"
                  className="w-full rounded-none font-bold uppercase tracking-wide"
                  disabled={loading || !email.trim()}
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-password-success">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Check your inbox</h2>
                  <p className="text-sm text-muted-foreground">We&apos;ve sent you instructions</p>
                </div>
              </div>

              <div className="bg-muted/30 p-4 mb-6 flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm">If an account exists for:</p>
                  <p className="text-sm font-mono font-medium mt-1">{submittedEmail}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    you&apos;ll receive a password reset link shortly. The link will expire in 15 minutes.
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mb-4">
                Didn&apos;t get an email? Check your spam folder, or{' '}
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-primary hover:underline font-medium"
                  data-testid="try-again-button"
                >
                  try again
                </button>
                .
              </p>

              <Link to="/login" className="block">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-none font-bold uppercase tracking-wide"
                  data-testid="return-to-login-button"
                >
                  Return to login
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
